/* eslint-disable @typescript-eslint/no-non-null-assertion */
import {
  AdminSpinner,
  Div,
  ErrorToast,
  H5,
  ListSpinner,
  Tab,
  exportTransactionReport,
  fetchAndDownloadCsvFile,
  getAllTransaction,
  getSumTransactionVDrive,
  getSumTransactionVGrubs,
  handleSetUrlSearchParams,
  isAdmin,
  transactionsNoData,
  reportError,
} from '@vgrubs/components';
import {
  getPartnerAllTransaction,
  getPartnerExportReport,
  getPartnerSumTransactionVDrive,
  getPartnerSumTransactionVGrubs,
} from 'components/src/api/partner/wallet.partner-api';
import {
  IncomeStatus,
  IncomeType,
  NewTransactionResponse,
} from 'components/src/types/api/wallet.types';
import {
  TransactionType,
  WalletReportType,
} from 'components/src/types/wallet.types';
import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { useParams } from 'react-router-dom';
import { groupTransactionsByDate, useTransactionsDateRange } from './helpers';
import {
  getIncomeStatusOptions,
  getIncomeTypeOptions,
  TransactionsExportConfirmModal,
  TransactionsPageHeader,
} from './TransactionComponents';
import { TransactionsVDrive } from './TransactionsVDrive';
import { TransactionsVGrubs } from './TransactionsVGrubs';
import { showNoDataText } from './TransactonsStyled';
import { NoDataComponent } from 'components/src/adminComponents/NoDataComponent/NoDataComponent';

export const Transactions = () => {
  const [loading, setLoading] = useState(false);
  const [loadingSum, setLoadingSum] = useState(false);
  const [transactions, setTransactions] = useState<NewTransactionResponse[]>(
    []
  );
  const [transactionsByDate, setTransactionsByDate] = useState<
    { date: string; items: NewTransactionResponse[] }[]
  >([]);
  const [sumTransactions, setSumTransactions] = useState({
    vGrubsIncome: 0,
    vGrubsWithdrawals: 0,
    vDriveDeposits: 0,
    vDriveTransactions: 0,
    lastVGrubsIncome: 0,
    lastVGrubsWithdrawal: 0,
    lastVDriveDeposit: 0,
    vDriveDepositsReports: 0,
    userName: '',
  });

  const limit = 50;
  const [hasMore, setHasMore] = useState(true);
  const [ref, inView] = useInView();
  const { id: userId } = useParams() as { id?: string };
  const [exportConfirmOpen, setExportConfirmOpen] = useState(false);

  async function getTransactions(
    _isWithScroll: boolean,
    hasMore: boolean,
    page: number,
    isFilter: boolean
  ) {
    if (isAdmin && !userId) return;
    if (!hasMore) return;

    try {
      setLoading(true);
      const isIncomes = vType === 'vGrubs' && type === 'Incomes';
      const { data } = !isAdmin
        ? await getPartnerAllTransaction({
            from,
            to,
            type: (vType + type) as TransactionType,
            page,
            limit,
            incomeStatus: isIncomes ? incomeStatus ?? undefined : undefined,
            incomeType: isIncomes ? incomeType ?? undefined : undefined,
          })
        : await getAllTransaction(userId!, {
            from,
            to,
            type: (vType + type) as TransactionType,
            page,
            limit,
            incomeStatus: isIncomes ? incomeStatus ?? undefined : undefined,
            incomeType: isIncomes ? incomeType ?? undefined : undefined,
          });

      const fetchedTransactions = data.data;

      if (fetchedTransactions.length === limit) {
        setPage(Number(page) + 1);
        setHasMore(true);
      } else {
        setHasMore(false);
      }

      // Combine and group the transactions as an array by date
      const combinedTransactions = isFilter
        ? fetchedTransactions
        : [...transactions, ...fetchedTransactions];

      const grouped = groupTransactionsByDate(combinedTransactions);
      setTransactions(combinedTransactions);
      setTransactionsByDate(grouped);

      setLoading(false);
    } catch (error) {
      reportError(error);
      setLoading(false);
    }
  }

  async function getTransactionsSum() {
    try {
      setLoadingSum(true);

      if (vType === 'vGrubs') {
        const res = !isAdmin
          ? await getPartnerSumTransactionVGrubs({
              from,
              to,
              incomeStatus: sumIncomeStatus,
            })
          : await getSumTransactionVGrubs(userId!, {
              from,
              to,
              incomeStatus: sumIncomeStatus,
            });

        const {
          vGrubsIncome,
          vGrubsWithdrawals,
          lastVGrubsIncome,
          lastVGrubsWithdrawal,
          userName,
        } = res.data.data;

        setSumTransactions((prev) => {
          return {
            ...prev,
            vGrubsIncome: vGrubsIncome || 0,
            vGrubsWithdrawals: vGrubsWithdrawals || 0,
            lastVGrubsIncome: lastVGrubsIncome || 0,
            lastVGrubsWithdrawal: lastVGrubsWithdrawal || 0,
            userName: userName || '',
          };
        });
      }

      if (vType === 'vDrive') {
        const res = !isAdmin
          ? await getPartnerSumTransactionVDrive({
              from,
              to,
            })
          : await getSumTransactionVDrive(userId!, {
              from,
              to,
            });

        const {
          vDriveDeposits,
          vDriveTransactions,
          lastVDriveDeposit,
          vDriveDepositsReports,
          userName,
        } = res.data.data;

        setSumTransactions((prev) => {
          return {
            ...prev,
            vDriveDeposits: vDriveDeposits || 0,
            vDriveTransactions: vDriveTransactions || 0,
            lastVDriveDeposit: lastVDriveDeposit || 0,
            vDriveDepositsReports: vDriveDepositsReports || 0,
            userName: userName || '',
          };
        });
      }

      setLoadingSum(false);
    } catch (error) {
      reportError(error);
      setLoading(false);
    }
  }

  async function exportTransactions() {
    const fromISO = DateTime.fromISO(from).toISODate() || '';
    const toISO = DateTime.fromISO(to).toISODate() || '';

    try {
      setLoading(true);
      const { data } = !isAdmin
        ? await getPartnerExportReport({
            from: fromISO,
            to: toISO,
            type: vType as WalletReportType,
          })
        : await exportTransactionReport(userId!, {
            from: fromISO,
            to: toISO,
            type: vType as WalletReportType,
          });

      if (Array.isArray(data.data) && data.data.length > 0) {
        for (const url of data.data) {
          await fetchAndDownloadCsvFile(url); // Use fetch to handle properly
        }
      } else {
        ErrorToast('No files available for download.');
      }

      setLoading(false);
    } catch (error) {
      reportError(error);
      setLoading(false);
    }
  }

  const {
    from,
    to,
    page,
    setPage,
    dateInput,
    handleDateChange,
    handleDateApply,
    clearDate,
    hasDateFilter,
    dateLabel,
    searchParams,
    setSearchParams,
  } = useTransactionsDateRange();

  const type = searchParams.get('type') ?? ('Incomes' as string);
  const vType = searchParams.get('vType') ?? ('vGrubs' as WalletReportType);
  const incomeStatus = searchParams.get('incomeStatus') as IncomeStatus | null;
  const incomeType = searchParams.get('incomeType') as IncomeType | null;

  // The status filter only applies to vGrubs incomes, and whatever we send to
  // new-transactions has to go to the sum call too - otherwise the Total Income
  // card and the rows under it disagree.
  const sumIncomeStatus =
    vType === 'vGrubs' && type === 'Incomes'
      ? incomeStatus ?? undefined
      : undefined;

  const setType = (type: string, replace = false) => {
    handleSetUrlSearchParams('type', type, setSearchParams, replace);
  };

  const setVType = (vType: string | null) => {
    handleSetUrlSearchParams('vType', vType, setSearchParams);
  };

  const setIncomeStatus = (next: string | null) =>
    handleSetUrlSearchParams('incomeStatus', next, setSearchParams);
  const setIncomeType = (next: string | null) =>
    handleSetUrlSearchParams('incomeType', next, setSearchParams);

  useEffect(() => {
    setPage(1);
    setTransactions([]);
    setHasMore(true);
    getTransactions(false, true, 1, true);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, to, type, incomeStatus, incomeType]);

  useEffect(() => {
    const isIncomes = vType === 'vGrubs' && type === 'Incomes';
    if (!isIncomes) {
      if (incomeStatus) setIncomeStatus(null);
      if (incomeType) setIncomeType(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vType, type]);

  useEffect(() => {
    if (inView && transactions.length > 0) {
      getTransactions(true, hasMore, page, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inView]);

  useEffect(() => {
    if (vType === 'vGrubs' && type !== 'Incomes' && type !== 'Withdrawals') {
      setType('Incomes', true);
    }
    if (vType === 'vDrive' && type !== 'Deposits' && type !== 'Transactions') {
      setType('Deposits', true);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vType]);

  useEffect(() => {
    getTransactionsSum();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, to, vType, sumIncomeStatus]);

  return (
    <div>
      {loading && transactions.length === 0 && <AdminSpinner />}
      <TransactionsPageHeader
        dateInput={dateInput}
        handleDateChange={handleDateChange}
        handleDateApply={handleDateApply}
        clearDate={clearDate}
        hasDateFilter={hasDateFilter}
        dateLabel={dateLabel}
        onExport={() => setExportConfirmOpen(true)}
        incomeStatus={incomeStatus}
        setIncomeStatus={setIncomeStatus}
        incomeType={incomeType}
        setIncomeType={setIncomeType}
        showIncomeFilters={vType === 'vGrubs' && type === 'Incomes'}
        incomeStatusOptions={getIncomeStatusOptions()}
        incomeTypeOptions={getIncomeTypeOptions()}
        userName={sumTransactions.userName}
      />
      <TransactionsExportConfirmModal
        isOpen={exportConfirmOpen}
        closeModal={() => setExportConfirmOpen(false)}
        onConfirm={() => exportTransactions()}
        dateLabel={dateLabel}
      />
      <Div
        $flex
        $between
        $alignCenter
        $gap="20px"
        $gapSM="16px"
        $mb="32"
        $mSM="20px 0"
      >
        <Div $flex $gap="16px" $width="100%">
          <Tab
            $widthSM="calc(50% - 8px)"
            selected={vType === 'vGrubs'}
            onClick={() => setVType('vGrubs')}
          >
            vGrubs
          </Tab>
          <Tab
            $widthSM="calc(50% - 8px)"
            selected={vType === 'vDrive'}
            onClick={() => setVType('vDrive')}
          >
            vDrive
          </Tab>
        </Div>
      </Div>
      <Div $desktop $mb="24">
        <H5 $mb="0">{dateLabel}</H5>
      </Div>
      {vType === 'vGrubs' && (
        <TransactionsVGrubs
          transactions={transactionsByDate}
          sumTransactions={sumTransactions}
          setType={setType}
          type={type}
          loadingSum={loadingSum}
          page={Number(page) || 1}
        />
      )}
      {vType === 'vDrive' && (
        <TransactionsVDrive
          transactions={transactionsByDate}
          sumTransactions={sumTransactions}
          setType={setType}
          type={type}
          loadingSum={loadingSum}
          page={Number(page) || 1}
        />
      )}
      {transactions.length < 1 && !loading && (
        <NoDataComponent
          image={transactionsNoData}
          title={showNoDataText(type)}
          heightSM="calc(100vh - 500px)"
        />
      )}
      <ListSpinner
        loading={loading && transactions.length > 0}
        observerRef={ref}
      />
    </div>
  );
};
